import * as THREE from 'three'
import { toScreenPosition } from '../utils'

export type MouseLookMode = 'continuous' | 'onMove'

interface MouseLookOptions {
  mode?: MouseLookMode
  renderer: THREE.WebGLRenderer | any
  camera: THREE.Camera
  height?: number
}

export class MouseLookController {

  private object: THREE.Object3D
  private camera: THREE.Camera
  private renderer: THREE.WebGLRenderer | any
  private mode: MouseLookMode
  private height: number
  private mouse = new THREE.Vector2()
  private enabled = false

  private overrideUntil = 0  // timestamp in ms

  private boundMove = (e: MouseEvent) => this.onMouseMove(e)

  constructor(
    object: THREE.Object3D,
    options: MouseLookOptions
  ) {
    this.object = object
    this.camera = options.camera
    this.renderer = options.renderer
    this.mode = options.mode ?? 'onMove'
    this.height = options.height ?? 8
  }

  enable(): void {
    if (this.enabled) return
    this.enabled = true

    window.addEventListener('mousemove', this.boundMove)
  }

  disable(): void {
    if (!this.enabled) return
    this.enabled = false

    window.removeEventListener('mousemove', this.boundMove)
  }

  suppressFor(ms: number): void {
    this.overrideUntil = performance.now() + ms
  }

  update(): void {
    if (!this.enabled) return
    if (this.mode === 'continuous') {
      this.lookAtMouse()
    }
  }

  private onMouseMove(event: MouseEvent): void {
    this.mouse.x = event.clientX
    this.mouse.y = event.clientY

    if (this.mode === 'onMove') {
      this.lookAtMouse()
    }
  }

  private lookAtMouse(): void {
    if (performance.now() < this.overrideUntil) return  // 👈 skip if suppressed

    const { x, y } = toScreenPosition(this.object, this.camera, this.renderer)

    const objectX = x - window.innerWidth / 2
    const objectY = y - window.innerHeight / 2

    const mouseX = this.mouse.x - window.innerWidth / 2
    const mouseY = this.mouse.y - window.innerHeight / 2

    this.object.lookAt(
      mouseX - objectX,
      this.height,
      mouseY - objectY
    )
  }
}
